if (!customElements.get('aplovio-information-group')) {
  customElements.define('aplovio-information-group', class extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.abort = new AbortController();
      this.items = this.querySelector('[data-information-items]');
      this.tabs = this.querySelector('[role="tablist"]');
      this.media = matchMedia('(max-width:989px)');
      const options = {signal: this.abort.signal};
      this.media.addEventListener('change', () => this.configure(), options);
      this.tabs.addEventListener('click', event => {
        const button = event.target.closest('[role="tab"]');
        if (button) this.select(button.getAttribute('aria-controls'));
      }, options);
      this.tabs.addEventListener('keydown', event => this.onKeydown(event), options);
      document.addEventListener('shopify:block:select', event => {
        const view = event.target.closest?.('.ap-information-view');
        if (this.panes?.includes(view)) this.select(view.id);
      }, options);
      this.observer = new MutationObserver(() => this.schedule());
      this.observer.observe(this.items, {childList: true, subtree: true, attributes: true, attributeFilter: ['hidden']});
      customElements.whenDefined('aplovio-information').then(() => {if (this.isConnected) this.configure();});
    }
    schedule() {
      if (!this.frame) this.frame = requestAnimationFrame(() => {this.frame = 0; this.configure();});
    }
    configure() {
      const all = [...this.items.children].filter(node => node.matches('.ap-information-view'));
      this.panes = all.filter(node => !node.hidden);
      this.hidden = this.panes.length === 0 && !this.hasAttribute('data-design-mode');
      const mode = this.media.matches && this.dataset.mobile !== 'same' ? this.dataset.mobile : this.dataset.desktop;
      const tabbed = mode === 'tabs' && this.panes.length > 0;
      this.dataset.mode = tabbed ? 'tabs' : mode;
      this.tabs.hidden = !tabbed;
      const signature = this.panes.map(node => `${node.id}:${node.querySelector('.ap-information__trigger')?.innerHTML}`).join('|');
      if (signature !== this.signature) {
        this.signature = signature;
        const buttons = this.panes.map(view => {
          const info = view.querySelector('aplovio-information');
          const button = document.createElement('button');
          button.type = 'button';
          button.id = `${view.id}-tab`;
          button.setAttribute('role', 'tab');
          button.setAttribute('aria-controls', view.id);
          const icon = info.querySelector('.ap-information__trigger > .ap-content-icon');
          if (icon) button.append(icon.cloneNode(true));
          const label = document.createElement('span');
          label.textContent = info.querySelector('.ap-information__heading').textContent;
          button.append(label);
          return button;
        });
        this.tabs.replaceChildren(...buttons);
      }
      for (const view of all) {
        const info = view.querySelector('aplovio-information');
        info.setGroupMode?.(tabbed ? 'inline' : mode === 'individual' ? null : mode);
        if (tabbed && !view.hidden) {
          view.setAttribute('role', 'tabpanel');
          view.setAttribute('aria-labelledby', `${view.id}-tab`);
          view.tabIndex = 0;
        } else {
          view.removeAttribute('role');
          view.removeAttribute('aria-labelledby');
          view.removeAttribute('tabindex');
          view.removeAttribute('data-tab-active');
          view.inert = false;
        }
      }
      if (tabbed) this.select(this.panes.some(view => view.id === this.active) ? this.active : this.panes[0].id);
    }
    select(id, focus = false) {
      if (this.dataset.mode !== 'tabs') return;
      this.active = id;
      for (const view of this.panes) {
        const active = view.id === id;
        view.dataset.tabActive = String(active);
        view.inert = !active;
      }
      for (const button of this.tabs.children) {
        const active = button.getAttribute('aria-controls') === id;
        button.setAttribute('aria-selected', String(active));
        button.tabIndex = active ? 0 : -1;
        if (active && focus) {button.focus(); button.scrollIntoView({block: 'nearest', inline: 'nearest'});}
      }
    }
    onKeydown(event) {
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      const buttons = [...this.tabs.children];
      const current = buttons.indexOf(event.target.closest('[role="tab"]'));
      if (current < 0) return;
      const direction = getComputedStyle(this).direction === 'rtl' ? -1 : 1;
      let next = event.key === 'Home' ? 0 : event.key === 'End' ? buttons.length - 1 : (current + (event.key === 'ArrowRight' ? direction : -direction) + buttons.length) % buttons.length;
      event.preventDefault();
      this.select(buttons[next].getAttribute('aria-controls'), true);
    }
    disconnectedCallback() {
      this.abort?.abort(); this.abort = null;
      this.observer?.disconnect(); cancelAnimationFrame(this.frame); this.frame = 0;
      for (const view of this.panes || []) {
        view.removeAttribute('role'); view.removeAttribute('aria-labelledby'); view.removeAttribute('tabindex'); view.removeAttribute('data-tab-active'); view.inert = false;
        view.querySelector('aplovio-information')?.setGroupMode?.(null);
      }
    }
  });
}
